import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import {
  Play,
  X,
  ArrowRight,
  Sparkles,
  Shield,
  Layers,
  Target,
  AlertTriangle,
  FileText,
  Calendar,
  BarChart3,
  TrendingUp,
  Building2,
  Siren,
  CheckCircle2,
  Circle,
  ListChecks,
  MapPin,
} from "lucide-react";

/* ── Quick-Start cards (existing splash) ── */
const DEMO_FEATURES = [
  {
    id: "asset",
    title: "Add a Critical Asset",
    description: "Walk through the 5-step TurboTax wizard to register, classify, and assign ownership of a business asset.",
    icon: Layers,
    color: "from-sky-600 to-cyan-500",
    bgColor: "bg-sky-50",
    borderColor: "border-sky-200",
    textColor: "text-sky-700",
    path: "/asset-inventory?autoWizard=true",
    steps: "5 steps: Identity → Classification → Ownership → Location → Review",
  },
  {
    id: "recovery",
    title: "Generate a Recovery Plan",
    description: "Use the AI-assisted wizard to scope a plan, link risks, define recovery tasks, and generate documentation.",
    icon: Shield,
    color: "from-teal-500 to-emerald-500",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
    textColor: "text-teal-700",
    path: "/recovery-plans?autoWizard=true",
    steps: "5 steps: Plan Type → Scope → Risk → Tasks → Review",
  },
  {
    id: "governance",
    title: "Configure Governance",
    description: "Step through the BCM mandate, authority matrix, impact matrix, and sensitivity anchor — one section at a time.",
    icon: Target,
    color: "from-indigo-500 to-purple-500",
    bgColor: "bg-indigo-50",
    borderColor: "border-indigo-200",
    textColor: "text-indigo-700",
    path: "/governance",
    steps: "4 steps: Mandate → Authority → Impact → Sensitivity",
  },
];

/* ── Guided Tour checklist (PDCA-ordered) ── */
const PHASE_STYLES = {
  PLAN:  { bg: "bg-blue-50",    border: "border-blue-200",   text: "text-blue-700",    pill: "bg-blue-100 text-blue-700"    },
  DO:    { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", pill: "bg-emerald-100 text-emerald-700" },
  CHECK: { bg: "bg-amber-50",  border: "border-amber-200",  text: "text-amber-700",   pill: "bg-amber-100 text-amber-700"  },
  ACT:   { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700",  pill: "bg-purple-100 text-purple-700" },
};

const DEMO_CHECKLIST = [
  { id: "org",        phase: "PLAN",  title: "Review Organization Structure",    desc: "Explore departments, locations, and team members set up for the demo company.",      path: "/org-management",         icon: Building2    },
  { id: "bia",        phase: "PLAN",  title: "Complete an Operational BIA",      desc: "Walk through the BIA wizard — assess impact, map dependencies, set RTO/RPO.",       path: "/operational-bia",        icon: Target       },
  { id: "threat",     phase: "PLAN",  title: "Configure a Threat Profile",       desc: "Open the 4-step wizard to rate a threat, set vulnerability, and map asset types.",    path: "/threat-profiling",       icon: MapPin       },
  { id: "risk",       phase: "PLAN",  title: "Run a Risk Assessment",            desc: "Create a risk entry using the 5-step wizard — process, location, threat, vulnerability, review.", path: "/risk-assessment", icon: AlertTriangle },
  { id: "recovery",   phase: "DO",    title: "Generate a Recovery Plan",         desc: "Use the AI-powered wizard to create a recovery plan with auto-generated tasks.",     path: "/recovery-plans",         icon: FileText     },
  { id: "strategy",   phase: "DO",    title: "Define a Recovery Strategy",       desc: "Set recovery targets, cost estimates, and strategy details for a process.",           path: "/recovery-strategy",      icon: Layers       },
  { id: "erp",        phase: "DO",    title: "Explore Emergency Response",       desc: "Review the multi-step ERP wizard — crisis teams, escalation, communication.",        path: "/emergency-response",     icon: Siren        },
  { id: "exercise",   phase: "CHECK", title: "Schedule an Exercise",             desc: "Plan a tabletop, simulation, or full-scale exercise for your recovery plans.",        path: "/exercises",              icon: Calendar     },
  { id: "compliance", phase: "CHECK", title: "Review Compliance Posture",        desc: "Check your ISO 22301 compliance score across all PDCA phases.",                       path: "/performance-compliance", icon: BarChart3    },
  { id: "improve",    phase: "ACT",   title: "Review Improvement Actions",       desc: "Track PACA items, corrective actions, and continual improvement tasks.",              path: "/improvement-actions",    icon: TrendingUp   },
];

/* ── localStorage helpers ── */
const getStorageKey = (userId) => `demo_checklist_${userId || "demo"}`;

const loadProgress = (userId) => {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const saveProgress = (userId, progress) => {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(progress));
};

/* ── Exported helper for sidebar progress display ── */
export const getDemoChecklistProgress = (userId) => {
  const progress = loadProgress(userId);
  const completed = DEMO_CHECKLIST.filter(t => progress[t.id]).length;
  return { completed, total: DEMO_CHECKLIST.length, pct: Math.round((completed / DEMO_CHECKLIST.length) * 100) };
};

/* ── Main component ── */
export default function DemoWelcomeOverlay() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [activeTab, setActiveTab] = useState("welcome"); // "welcome" | "checklist"
  const [progress, setProgress] = useState({});

  const userId = user?.id || user?.user_id || "demo";
  const isDemo = user?.is_demo || user?.plan_type === "demo";

  // Load progress on mount
  useEffect(() => {
    if (!isDemo) return;
    setProgress(loadProgress(userId));
  }, [isDemo, userId]);

  // Auto-detect page visits → mark as completed
  useEffect(() => {
    if (!isDemo) return;
    const basePath = location.pathname;
    const match = DEMO_CHECKLIST.find(t => basePath === t.path || basePath.startsWith(t.path + "/"));
    if (match) {
      // Load fresh progress from localStorage to avoid race conditions
      const currentProgress = loadProgress(userId);
      if (!currentProgress[match.id]) {
        const next = { ...currentProgress, [match.id]: true };
        setProgress(next);
        saveProgress(userId, next);
      }
    }
  }, [location.pathname, isDemo, userId]);

  // First-time splash
  useEffect(() => {
    if (!isDemo) return;
    const key = `demo_welcome_seen_${userId}`;
    if (!localStorage.getItem(key)) {
      setVisible(true);
      requestAnimationFrame(() => setAnimateIn(true));
    }
  }, [isDemo, userId]);

  // Listen for replay event (welcome tab) and checklist event (checklist tab)
  useEffect(() => {
    const handleReplay = () => {
      setActiveTab("welcome");
      setVisible(true);
      requestAnimationFrame(() => setAnimateIn(true));
    };
    const handleChecklist = () => {
      setActiveTab("checklist");
      setProgress(loadProgress(userId));
      setVisible(true);
      requestAnimationFrame(() => setAnimateIn(true));
    };
    window.addEventListener("replay-demo-tour", handleReplay);
    window.addEventListener("open-demo-checklist", handleChecklist);
    return () => {
      window.removeEventListener("replay-demo-tour", handleReplay);
      window.removeEventListener("open-demo-checklist", handleChecklist);
    };
  }, [userId]);

  const dismiss = useCallback(() => {
    setAnimateIn(false);
    setTimeout(() => {
      setVisible(false);
      const key = `demo_welcome_seen_${userId}`;
      localStorage.setItem(key, "true");
    }, 300);
  }, [userId]);

  const handleTryIt = useCallback((path) => {
    dismiss();
    setTimeout(() => navigate(path), 350);
  }, [dismiss, navigate]);

  const toggleCheck = useCallback((taskId) => {
    setProgress(prev => {
      const next = { ...prev, [taskId]: !prev[taskId] };
      saveProgress(userId, next);
      return next;
    });
  }, [userId]);

  if (!visible) return null;

  const completedCount = DEMO_CHECKLIST.filter(t => progress[t.id]).length;
  const pct = Math.round((completedCount / DEMO_CHECKLIST.length) * 100);

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-all duration-300 ${
        animateIn ? "bg-black/60 backdrop-blur-sm" : "bg-black/0"
      }`}
      data-testid="demo-welcome-overlay"
    >
      <div
        className={`relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-500 ${
          animateIn ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-8"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header gradient bar */}
        <div className="h-1.5 bg-gradient-to-r from-sky-600 via-teal-500 to-indigo-500" />

        {/* Close button */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors z-10"
          data-testid="demo-welcome-close"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>

        {/* Header with Tabs */}
        <div className="p-6 pb-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-600 to-teal-500 flex items-center justify-center shadow-lg shadow-sky-200/50">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Welcome to the ResiliencyHUB Demo</h2>
              <p className="text-sm text-slate-500">Experience the full BCM platform</p>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-1 mt-2 border-b border-slate-100">
            <button
              onClick={() => setActiveTab("welcome")}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                activeTab === "welcome"
                  ? "border-sky-600 text-sky-700"
                  : "border-transparent text-slate-400 hover:text-slate-600"
              }`}
              data-testid="tab-quick-start"
            >
              <Play className="w-3.5 h-3.5" />
              Quick Start
            </button>
            <button
              onClick={() => setActiveTab("checklist")}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                activeTab === "checklist"
                  ? "border-sky-600 text-sky-700"
                  : "border-transparent text-slate-400 hover:text-slate-600"
              }`}
              data-testid="tab-guided-tour"
            >
              <ListChecks className="w-3.5 h-3.5" />
              Guided Tour
              {completedCount > 0 && (
                <span className="ml-1 text-[0.625rem] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-bold">
                  {completedCount}/{DEMO_CHECKLIST.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ── Tab Content: Quick Start ── */}
        {activeTab === "welcome" && (
          <>
            <div className="px-6 pt-4 pb-2">
              <p className="text-sm text-slate-600 leading-relaxed">
                This sandbox is pre-loaded with sample data. Try any of the guided workflows below
                to see how ResiliencyHUB walks users through complex BCM tasks — one step at a time.
              </p>
            </div>
            <div className="px-6 pt-2 space-y-3">
              {DEMO_FEATURES.map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={feature.id}
                    className={`group relative flex items-center gap-4 p-4 rounded-xl border ${feature.borderColor} ${feature.bgColor} hover:shadow-md transition-all duration-200 cursor-pointer`}
                    onClick={() => handleTryIt(feature.path)}
                    data-testid={`demo-try-${feature.id}`}
                  >
                    <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 text-sm">{feature.title}</h3>
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-1">{feature.description}</p>
                      <p className={`text-[0.6875rem] ${feature.textColor} mt-1 font-medium`}>{feature.steps}</p>
                    </div>
                    <ArrowRight className={`w-5 h-5 ${feature.textColor} opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all`} />
                  </div>
                );
              })}
            </div>
            <div className="p-6 pt-4 flex items-center justify-between">
              <button
                onClick={() => setActiveTab("checklist")}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium transition-colors"
                data-testid="demo-switch-to-checklist"
              >
                Or follow the Guided Tour →
              </button>
              <Button onClick={dismiss} variant="outline" size="sm" className="text-xs" data-testid="demo-welcome-dismiss">
                <Play className="w-3.5 h-3.5 mr-1.5" />
                Start Exploring
              </Button>
            </div>
          </>
        )}

        {/* ── Tab Content: Guided Tour Checklist ── */}
        {activeTab === "checklist" && (
          <>
            <div className="px-6 pt-4 pb-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-600">
                  Follow this PDCA-ordered tour to explore the platform's key capabilities.
                </p>
                <span className="text-xs font-bold text-sky-700 whitespace-nowrap ml-3">{completedCount}/{DEMO_CHECKLIST.length}</span>
              </div>
              <Progress value={pct} className="h-2" data-testid="checklist-progress-bar" />
              {pct === 100 && (
                <p className="text-xs text-emerald-600 font-medium mt-1.5 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> All done — you've explored the full platform!
                </p>
              )}
            </div>

            <div className="px-6 pt-1 pb-2 max-h-[48vh] overflow-y-auto space-y-1" data-testid="checklist-tasks">
              {DEMO_CHECKLIST.map((task) => {
                const Icon = task.icon;
                const done = !!progress[task.id];
                const style = PHASE_STYLES[task.phase];
                return (
                  <div
                    key={task.id}
                    className={`group flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      done
                        ? "bg-slate-50 border-slate-200 opacity-70"
                        : `${style.bg} ${style.border} hover:shadow-sm`
                    }`}
                    data-testid={`checklist-task-${task.id}`}
                  >
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleCheck(task.id)}
                      className="flex-shrink-0"
                      data-testid={`check-${task.id}`}
                    >
                      {done ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <Circle className="w-5 h-5 text-slate-300 group-hover:text-slate-400 transition-colors" />
                      )}
                    </button>

                    {/* Icon */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${done ? 'bg-slate-200' : style.pill}`}>
                      <Icon className={`w-4 h-4 ${done ? 'text-slate-400' : ''}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${done ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                          {task.title}
                        </span>
                        <span className={`text-[0.6rem] px-1.5 py-0.5 rounded-full font-bold ${style.pill}`}>
                          {task.phase}
                        </span>
                      </div>
                      <p className={`text-xs mt-0.5 ${done ? 'text-slate-400' : 'text-slate-500'}`}>{task.desc}</p>
                    </div>

                    {/* Go button */}
                    {!done && (
                      <button
                        onClick={() => handleTryIt(task.path)}
                        className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all opacity-0 group-hover:opacity-100 ${style.pill} hover:shadow-sm`}
                        data-testid={`go-${task.id}`}
                      >
                        Go <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="p-6 pt-3 flex items-center justify-between border-t border-slate-100">
              <button
                onClick={() => setActiveTab("welcome")}
                className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              >
                ← Quick Start
              </button>
              <Button onClick={dismiss} variant="outline" size="sm" className="text-xs" data-testid="checklist-dismiss">
                Continue Exploring
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
